import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';

export interface ScheduleSlot {
  day: number;
  hour: number;
  minute: number;
}

export interface RoomPatterns {
  horizontal: boolean;
  vertical: boolean;
  diagonal: boolean;
  singleCorner: boolean;
  singleMiddle: boolean;
  middleCross: boolean;
  full: boolean;
}

export interface GameRoom {
  _id: string;
  roomId: string;
  betAmount: number;
  description: string;
  rules: string | null;
  status: 'waiting' | 'running' | 'finished';
  isActive: boolean;
  sequence: number[];
  calledNumbers: number[];
  currentNumber: number | null;
  callIndex: number;
  nextCallAt: string | null;
  callIntervalSeconds: number;
  patternsRequiredToWin: number;
  maxCartelaCount: number;
  playersCount: number;
  reservedCartelas: number[];
  prizePool: number;
  commissionPercent: number;
  joinWindowEndsAt: string | null;
  isSuperBingo: boolean;
  scheduledStartAt: string | null;
  schedule: ScheduleSlot[];
  patterns: RoomPatterns;
  startedAt: string | null;
  winners: any[];
  winnerCallIndex: number | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGameRoomInput {
  roomId: string;
  betAmount: number;
  description?: string;
  rules?: string | null;
  commissionPercent?: number;
  callIntervalSeconds?: number;
  patternsRequiredToWin?: number;
  maxCartelaCount?: number;
  isSuperBingo?: boolean;
  schedule?: ScheduleSlot[];
  scheduledStartAt?: string | null;
  patterns?: Partial<RoomPatterns>;
  isActive?: boolean;
}

export type UpdateGameRoomInput = Partial<Omit<CreateGameRoomInput, 'roomId'>>;

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

class GameRoomService {
  async getAll(): Promise<GameRoom[]> {
    const res = await apiClient.get<ApiEnvelope<GameRoom[]>>(
      API_ENDPOINTS.GAME_ROOMS.GET_ALL
    );
    return res.data.data;
  }

  async getOne(id: string): Promise<GameRoom> {
    const res = await apiClient.get<ApiEnvelope<GameRoom>>(
      API_ENDPOINTS.GAME_ROOMS.GET_ONE(id)
    );
    return res.data.data;
  }

  async create(input: CreateGameRoomInput): Promise<GameRoom> {
    const res = await apiClient.post<ApiEnvelope<GameRoom>>(
      API_ENDPOINTS.GAME_ROOMS.CREATE,
      input
    );
    return res.data.data;
  }

  async update(id: string, input: UpdateGameRoomInput): Promise<GameRoom> {
    const res = await apiClient.put<ApiEnvelope<GameRoom>>(
      API_ENDPOINTS.GAME_ROOMS.UPDATE(id),
      input
    );
    return res.data.data;
  }

  async toggleActive(id: string): Promise<GameRoom> {
    const res = await apiClient.patch<ApiEnvelope<GameRoom>>(
      API_ENDPOINTS.GAME_ROOMS.TOGGLE_ACTIVE(id)
    );
    return res.data.data;
  }
}

const gameRoomService = new GameRoomService();
export default gameRoomService;